import { AppLocation } from 'src/navigation/appLocations'
import * as Routes from 'src/navigation/urls'

/**
 * My Wallet flat route tree (mirrors budgetsRouteTree); contextual subnav,
 * single-view detail — deviations documented in src/navigation/AGENTS.md.
 */

export interface MyWalletFamilyRouteNode {
  /** AppLocation that drives sidenav active state via locationHooks. */
  appLocation: AppLocation
  /** Exact URL pattern, e.g. `/mywallet/virtualcards`. */
  path: string
}

export const myWalletRouteTree = {
  family: {
    /** `/mywallet` — redirects to the first tab (Virtual Cards), legacy parity. */
    rootPath: Routes.myWalletUrl(),
    /**
     * Bare `/mywallet` lands on Virtual Cards (legacy redirect target); sidenav
     * highlight matches that landing route.
     */
    rootAppLocation: AppLocation.MyWalletVirtualCards,
    redirectTo: Routes.myWalletVirtualCardsUrl(),
    routes: [
      {
        appLocation: AppLocation.MyWalletVirtualCards,
        path: Routes.myWalletVirtualCardsUrl(),
      },
      {
        appLocation: AppLocation.MyWalletPhysicalCards,
        path: Routes.myWalletPhysicalCardsUrl(),
      },
      {
        appLocation: AppLocation.MyWalletTransactions,
        path: Routes.myWalletTransactionsURL(),
      },
      {
        appLocation: AppLocation.MyWalletBudgets,
        path: Routes.myWalletBudgetsURL(),
      },
      {
        // Reuses BudgetsRequests content under a My-Wallet-namespaced route
        // (MyWalletBudgetsPending), consumed only by this shell.
        appLocation: AppLocation.MyWalletBudgetsPending,
        path: Routes.myWalletBudgetsPendingURL(),
      },
      {
        // Reuses ApproverExpenseReports content; the legacy `/expenses` shell
        // never matched this `/mywallet/*` path, so this shell is its only live owner.
        appLocation: AppLocation.MyWalletReimbursements,
        path: Routes.submitExpensesUrl(),
      },
    ] as MyWalletFamilyRouteNode[],
  },
  detail: {
    /** `/mywallet/virtualcards/:id` — single full-page VirtualCardDetail (myWallet context). */
    rootAppLocation: AppLocation.MyWalletVirtualCardDetail,
    path: Routes.myWalletVirtualCardDetailUrl(':id'),
    /** Builds the navigable URL for a concrete virtual card id. */
    to: Routes.myWalletVirtualCardDetailUrl,
  },
}

/**
 * Family root + every static family path; enumerated so each out-specifies
 * the detail `/mywallet/virtualcards/:id` route at equal depth.
 */
export const myWalletFamilyGatePaths: string[] = [
  myWalletRouteTree.family.rootPath,
  ...myWalletRouteTree.family.routes.map((r) => r.path),
]

/**
 * Single 3-segment param path for the virtual-card detail — deeper than every
 * family path, so no widening/param-child matcher is needed.
 */
export const myWalletDetailGatePath: string = myWalletRouteTree.detail.path
